import { useEffect, useState } from 'react';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Badge } from '@cloudflare/kumo/components/badge';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { Empty } from '@cloudflare/kumo/components/empty';
import { Table } from '@cloudflare/kumo/components/table';
import {
  DialogRoot,
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@cloudflare/kumo/components/dialog';
import { ArrowCounterClockwise, EnvelopeSimple } from '@phosphor-icons/react';
import { api } from '@/lib/api';

// 内定通知メール (出展 OK) を一括送信する画面。
// status='checkedByAdmin' AND approvalEmailSent=false の出展者を自動リストし、
// 連絡が取れない人だけチェックを外して保留できる UX。
// ステージ管理はせず、同じ画面を複数回触れば「複数段階」運用が成立する。

interface PendingRow {
  id: string;
  applicationNumber: number;
  email: string;
  realName: string;
  exhibitorName: string;
  exhibitTitle: string;
  type: string;
  status: string;
  approvalEmailSent: boolean;
  exhibitDays: string[];
}

const TYPE_LABEL: Record<string, string> = {
  general: '一般',
  'student-university': '学生 (大学)',
  'student-high-school': '学生 (高校以下)',
  corporate: '企業',
};

export default function EmailsPage() {
  const [pending, setPending] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [previewing, setPreviewing] = useState<{
    eligibleCount: number;
    skippedCount: number;
  } | null>(null);
  const [sending, setSending] = useState(false);

  const selectedCount = selectedIds.size;
  const allChecked = pending.length > 0 && selectedCount === pending.length;
  const anyChecked = selectedCount > 0;

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: PendingRow[] }>(
        '/api/admin/exhibitors/pending-approval-email'
      );
      const rows = res.data.data ?? [];
      setPending(rows);
      // 初期表示は全選択 (admin が保留にしたい人だけチェックを外す UX)
      setSelectedIds(new Set(rows.map((r) => r.id)));
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pending.map((r) => r.id)));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openPreview = async () => {
    if (selectedCount === 0) return;
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/send-approval-emails', {
        exhibitorIds: Array.from(selectedIds),
        dryRun: true,
      });
      const data = (res.data?.data ?? res.data) as {
        eligibleCount: number;
        skippedCount: number;
      };
      setPreviewing({
        eligibleCount: data.eligibleCount,
        skippedCount: data.skippedCount,
      });
    } catch (e) {
      setError(extractError(e));
    }
  };

  const confirmSend = async () => {
    if (!previewing) return;
    setSending(true);
    setError(null);
    try {
      const res = await api.post('/api/admin/exhibitors/send-approval-emails', {
        exhibitorIds: Array.from(selectedIds),
        dryRun: false,
      });
      const data = (res.data?.data ?? res.data) as {
        queued: number;
        skipped?: Array<{ exhibitorId: string; reason: string }>;
      };
      setInfo(
        `内定通知メールを ${data.queued} 件キューに登録しました（毎分順次送信されます）${
          data.skipped?.length ? ` / スキップ ${data.skipped.length} 件` : ''
        }`
      );
      setPreviewing(null);
      await refresh();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-kumo-default">メール送信</h1>
          <p className="text-sm text-kumo-muted">
            内容確認が終わった出展者へ「出展 OK」の内定通知メールを一斉送信します。
            連絡が取れない人だけチェックを外して保留してください。
          </p>
        </div>
        <Button
          variant="secondary"
          icon={ArrowCounterClockwise}
          onClick={refresh}
          loading={loading}
        >
          再読み込み
        </Button>
      </div>

      {error && <Banner variant="error">{error}</Banner>}
      {info && <Banner variant="default">{info}</Banner>}

      <Banner variant="alert">
        <strong>送信後の取り消しはできません。</strong>{' '}
        「送信する」を押すと、選択中の出展者全員に即時メールが届きます。連絡が取れない方や保留したい方は、
        先に該当行のチェックを外してください。
      </Banner>

      <Surface>
        <div className="flex items-center justify-between border-b border-kumo-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold text-kumo-default">
              内定通知 未送信 ({pending.length})
            </h2>
            <p className="text-xs text-kumo-muted">
              内容確認済で、まだ内定通知メールを送っていない出展者を一覧しています。初期表示は全選択。
              連絡が取れない方だけチェックを外して保留してください。
            </p>
          </div>
          <Button
            icon={EnvelopeSimple}
            disabled={!anyChecked || loading}
            onClick={openPreview}
          >
            選択した {selectedCount} 件に内定通知メール送信
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader />
          </div>
        ) : pending.length === 0 ? (
          <Empty
            title="送信対象はありません"
            description="内容確認済で未送信の出展者はいなくなりました。"
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Head>
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    aria-label="すべて選択"
                  />
                </Table.Head>
                <Table.Head>出展者番号</Table.Head>
                <Table.Head>出展者</Table.Head>
                <Table.Head>形態</Table.Head>
                <Table.Head>出展タイトル</Table.Head>
                <Table.Head>出展日</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pending.map((row) => (
                <Table.Row key={row.id}>
                  <Table.Cell>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(row.id)}
                      onChange={() => toggleOne(row.id)}
                      aria-label={`#${row.applicationNumber} を選択`}
                    />
                  </Table.Cell>
                  <Table.Cell>#{row.applicationNumber}</Table.Cell>
                  <Table.Cell>
                    <div className="flex flex-col">
                      <span className="text-sm text-kumo-default">{row.exhibitorName}</span>
                      <span className="text-xs text-kumo-muted">
                        {row.realName} / {row.email}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{TYPE_LABEL[row.type] ?? row.type}</Table.Cell>
                  <Table.Cell>{row.exhibitTitle}</Table.Cell>
                  <Table.Cell>
                    <div className="flex gap-1">
                      {Array.isArray(row.exhibitDays) && row.exhibitDays.includes('saturday') && (
                        <Badge variant="outline">土</Badge>
                      )}
                      {Array.isArray(row.exhibitDays) && row.exhibitDays.includes('sunday') && (
                        <Badge variant="outline">日</Badge>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Surface>

      <DialogRoot
        role="alertdialog"
        open={previewing !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null);
        }}
      >
        <Dialog className="flex flex-col gap-4 p-6">
          <DialogTitle>内定通知メールを送信しますか?</DialogTitle>
          <DialogDescription>
            {previewing && (
              <>
                対象 <strong>{previewing.eligibleCount}</strong> 件に内定通知メールを即時送信します。
                <strong>送信後に取り消すことはできません。</strong>
                {previewing.skippedCount > 0 && (
                  <>
                    <br />
                    条件を満たさない {previewing.skippedCount} 件はスキップされます。
                  </>
                )}
              </>
            )}
          </DialogDescription>
          <div className="flex justify-end gap-2">
            <DialogClose
              render={(p) => (
                <Button {...p} variant="secondary">
                  キャンセル
                </Button>
              )}
            />
            <Button onClick={confirmSend} loading={sending} icon={EnvelopeSimple}>
              送信する
            </Button>
          </div>
        </Dialog>
      </DialogRoot>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const r = (e as { response?: { data?: { error?: string; message?: string } } }).response;
    return r?.data?.error ?? r?.data?.message ?? '送信に失敗しました';
  }
  return e instanceof Error ? e.message : '送信に失敗しました';
}
